import { Grid2 } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import ProfileCard from '@components/ProfileCard';
import SkeletonLoader from '@components/SkeletonLoader';

import { RoleName } from '@constants/role_name';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';
import { displayUserRoles, isDependant } from '@utils/role';

import Notifications from './Components/Notifications';
import RemoveAccessButton from './Components/RemoveAccessButton';
import RecordsAndFeaturesAccess from './Components/UserAccess';

interface UserAccessDrilldownProps {
  accessingUser?: Profile;
  ownerUser?: Profile;
  loading: boolean;
}

export default function UserAccessDrilldown({
  accessingUser,
  ownerUser,
  loading,
}: UserAccessDrilldownProps) {
  const { formatMessage } = useIntl();
  const ownerUserId = ownerUser?.user_id ?? '';
  const accessingUserId = accessingUser?.user_id ?? '';

  const roles = accessingUser
    ? displayUserRoles(accessingUser, ownerUser?.account_id ?? '')
    : [];

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12 }}>
        <Grid2
          container
          alignItems="center"
          justifyContent="space-between"
          rowGap={2}
        >
          <Grid2 size={{ sm: 6, xs: 12 }}>
            <ProfileCard
              loading={loading}
              name={formatName(
                accessingUser?.title,
                accessingUser?.first_name,
                accessingUser?.last_name
              )}
              profile_picture={accessingUser?.profile_picture}
              subtitle={roles
                .map((messageId) => formatMessage({ id: messageId }))
                .join(', ')}
            />
          </Grid2>
          <Grid2>
            <RemoveAccessButton
              accessingUserId={accessingUser?.user_id}
              ownerUserId={ownerUser?.user_id}
              disabled={loading}
            />
          </Grid2>
        </Grid2>
      </Grid2>

      {/** Hide notification section for practitioners */}
      {!accessingUser?.roles
        ?.map(({ role_name }) => role_name)
        .includes(RoleName.Practitioner) && (
        <Grid2 size={{ xs: 12 }}>
          <SkeletonLoader loading={loading} height={110}>
            <Notifications
              accessingUser={accessingUser}
              loading={loading}
              user={ownerUser}
            />
          </SkeletonLoader>
        </Grid2>
      )}

      {/** Hide access mgmt section for non-user patients (dependants) */}
      {accessingUser && !isDependant(accessingUser) && (
        <Grid2 size={{ xs: 12 }}>
          <SkeletonLoader loading={loading} height={200}>
            <RecordsAndFeaturesAccess
              accessingUserId={accessingUserId}
              ownerUserId={ownerUserId}
            />
          </SkeletonLoader>
        </Grid2>
      )}
    </Grid2>
  );
}
